import { useState, useTransition } from 'react';
// Ported from microsites/womens-conference/src/components/PrayerCard.tsx; the
// server action is now POST /api/events/{id}/prayer (same rules, same table).
import { sendPrayer } from '@/lib/api';
import type { Copy } from '../lib/copy';
import { LongRose } from './Botanical';
import { Sheet, SheetBand } from './Sheet';

/**
 * "PRAYER TAGS AND QR CODE"
 * =========================
 *
 * Straight off Misty's Friday run of show, alongside "prayer team down front
 * before and after each session". The team already planned to collect these on
 * paper tags; this is the same thing without a pen, and it is what a QR in the
 * lobby points at.
 *
 * A BUTTON THAT OPENS A SHEET, not a form lying open on the page.
 *
 * It was an open textarea, and that is the wrong posture for this. A box
 * waiting on a public page is a box anybody scrolling past reads as furniture,
 * and the woman who would actually use it is the one who least wants to type
 * into something sitting in the open — a lobby QR lands her on a page where the
 * next person over can see her screen. A sheet is a door: it closes, and what
 * is behind it is hers.
 *
 * It is also the shape the question box next to it already had, and these two
 * belong to the same moment at the bottom of the page. PAPER, WHERE THE
 * QUESTION BOX IS PAINTED. The pair should read as siblings without reading as
 * duplicates, and the difference is load-bearing rather than decorative: the
 * question box leads with the FACE of the woman who answers, because "who am I
 * emailing" is the thing somebody wants to know before asking about parking.
 * Prayer has no face on it on purpose. She is not writing to Misty, and a
 * portrait over this box would quietly tell her one specific person is about to
 * read her marriage.
 *
 * PRIVATE UNLESS SHE SAYS OTHERWISE, and the sharing controls are progressive:
 * the box is just a box until she has written something, and the second consent
 * does not exist until she has given the first. A form that opens with two
 * checkboxes about publication reads like a release form, and a woman writing
 * about her marriage should meet a place to write, not a permissions dialog.
 *
 * The privacy promise is the PLACEHOLDER rather than a paragraph above the box,
 * so it sits where she is about to type instead of being one more thing to read
 * first. It goes away once she starts writing, which is the trade: it is there
 * when she is deciding whether to write, not while she writes. It is said once
 * more on the button face, because that promise is what decides whether the
 * sheet gets opened at all, and inside is too late to make it.
 *
 * TWO CONSENTS, NOT ONE. "Share this" and "use my name" are different things to
 * agree to. One checkbox would make her choose between staying silent and being
 * identified in front of four hundred women, so the name only ever appears
 * where both are ticked — enforced in the action, in the data layer, and by a
 * check constraint, because this is the one thing here that must not go wrong.
 *
 * NOTHING SHE WRITES IS ECHOED BACK. On success the form is replaced by a
 * thank-you rather than her words in a "we received: …" panel, because a
 * request left on a lobby screen after she walks away is the same leak by a
 * slower route. CLOSING CLEARS IT for the same reason and more literally: the
 * lobby QR means the next person to pick up that phone, or to glance at it,
 * must not find a half-typed request still sitting in the box.
 */
export function PrayerCard({
  signedIn,
  copy,
  apiBaseUrl,
  eventId,
  pretend = false,
}: {
  signedIn: boolean;
  copy: Copy;
  apiBaseUrl: string;
  eventId: number;
  /** The time machine: nothing is sent. */
  pretend?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState('');
  const [shareable, setShareable] = useState(false);
  const [shareName, setShareName] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pending, startTransition] = useTransition();

  const written = request.trim().length > 0;

  const submit = () => {
    if (!written || pending) return;
    setFailed(false);
    startTransition(async () => {
      const ok = pretend
        ? true
        : await sendPrayer(apiBaseUrl, eventId, {
            request,
            name: signedIn ? undefined : name,
            email: signedIn ? undefined : email,
            shareable,
            shareName: shareable && shareName,
          }).then(
            () => true,
            () => false
          );
      if (ok) setDone(true);
      else setFailed(true);
    });
  };

  /**
   * Emptied on the way out, every time — not only after a successful send.
   * An abandoned draft is the more sensitive of the two states: she started
   * writing about something and thought better of it, and that is precisely
   * what must not still be on screen for whoever picks the phone up next.
   */
  const close = () => {
    setOpen(false);
    setRequest('');
    setShareable(false);
    setShareName(false);
    setName('');
    setEmail('');
    setDone(false);
    setFailed(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // The keynote card's paper and corner, so the page's cards read as one
        // set. h-full: beside the question card on a wide screen, the pair
        // stands at one height.
        className="wm-card wm-lift group relative flex h-full w-full items-center gap-5 overflow-hidden p-5 text-left sm:p-6"
      >
        {/* One cut rose laid in beside the words, no disc behind it. It
            straightens a little on hover, the card's only flourish. */}
        <span aria-hidden className="flex w-12 shrink-0 justify-center">
          <LongRose className="text-wm-plum-deep h-20 w-auto -rotate-[10deg] transition-transform duration-500 group-hover:-rotate-[3deg]" />
        </span>
        <span className="min-w-0 grow">
          <span className="wm-kicker block">{copy['prayer.eyebrow']}</span>
          <span className="wm-display mt-0.5 block text-3xl">{copy['prayer.heading']}</span>
          <span className="text-wm-sage-500 mt-1 block text-sm">{copy['prayer.invitation']}</span>
        </span>
        <span
          aria-hidden
          className="wm-glass text-wm-sage-600 group-hover:text-wm-sage-900 relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8h10M9 4l4 4-4 4" />
          </svg>
        </span>
      </button>

      <Sheet
        open={open}
        onClose={close}
        title={copy['prayer.heading']}
        band={
          <SheetBand
            kicker={copy['prayer.eyebrow']}
            title={done ? copy['prayer.thanks'] : copy['prayer.heading']}
            lead={<LongRose className="text-wm-blush h-16 w-auto -rotate-[10deg]" />}
          />
        }
      >
        <div className="flex flex-col gap-6 p-6 sm:px-7">
          {done ? (
            <p className="text-wm-sage-600 text-sm">{copy['prayer.thanksNote']}</p>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <label className="block">
                  <span className="sr-only">Your prayer request</span>
                  <textarea
                    value={request}
                    onChange={(e) => setRequest(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder={copy['prayer.placeholder']}
                    className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                  />
                </label>

                {/* Not shown until she has written something. The controls are
                    about what happens to her words, and they mean nothing
                    before there are any. */}
                {written ? (
                  <div className="flex flex-col gap-3">
                    {!signedIn ? (
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                          <span className="wm-kicker">{copy['prayer.nameLabel']}</span>
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                          />
                        </label>
                        <label className="block">
                          <span className="wm-kicker">{copy['prayer.emailLabel']}</span>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            className="border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none"
                          />
                        </label>
                      </div>
                    ) : null}

                    <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                      <input
                        type="checkbox"
                        checked={shareable}
                        onChange={(e) => {
                          setShareable(e.target.checked);
                          // Withdrawing the first consent withdraws the second.
                          // Leaving "use my name" ticked underneath an unticked
                          // "share it" is a state nobody meant and the database
                          // would refuse anyway.
                          if (!e.target.checked) setShareName(false);
                        }}
                        className="accent-wm-sage-800 mt-0.5 h-4 w-4"
                      />
                      <span>
                        {copy['prayer.shareable']}
                        <span className="text-wm-sage-500 block text-xs">
                          {copy['prayer.shareableNote']}
                        </span>
                      </span>
                    </label>

                    {/* The second consent does not exist until the first is given. */}
                    {shareable ? (
                      <label className="ml-6 flex cursor-pointer items-start gap-2.5 text-sm">
                        <input
                          type="checkbox"
                          checked={shareName}
                          onChange={(e) => setShareName(e.target.checked)}
                          className="accent-wm-sage-800 mt-0.5 h-4 w-4"
                        />
                        <span>
                          {copy['prayer.shareName']}
                          <span className="text-wm-sage-500 block text-xs">
                            {copy['prayer.shareNameNote']}
                          </span>
                        </span>
                      </label>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={submit}
                  disabled={!written || pending}
                  className="wm-lift bg-wm-marigold text-wm-sage-950 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                >
                  {pending ? copy['prayer.sending'] : copy['prayer.send']}
                </button>
                {failed ? (
                  <span role="status" className="text-wm-plum-deep text-xs">
                    {copy['prayer.failed']}
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>
      </Sheet>
    </>
  );
}
